import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../layouts/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { templateService, buonoService, Template } from '../../services/api';
import TemplatePreview from './TemplatePreview';
import './GeneraBuono.css';

/** Operatore per il dropdown: id univoco (teamId-memberId), name per visualizzazione e invio API */
interface OperatoreOption {
  id: string;
  name: string;
}

interface LocationState {
  templateId?: number;
}

const resolveImageUrl = (value?: string | null) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:') || value.startsWith('/')) {
    return value;
  }
  const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
  return `${apiBase}/storage/${value}`;
};

const GeneraBuonoCrea: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const state = location.state as LocationState | null;
  const templateId = state?.templateId;
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [operators, setOperators] = useState<OperatoreOption[]>([]);
  const [operatore, setOperatore] = useState('');
  const [mail, setMail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [regalo, setRegalo] = useState('');
  const [per, setPer] = useState('');
  const [da, setDa] = useState('');
  const [validoFino, setValidoFino] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showResultModal, setShowResultModal] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    if (templateId == null) {
      navigate('/admin/genera-buono', { replace: true });
      return;
    }
    let cancelled = false;
    templateService.getTemplates().then((data) => {
      if (!cancelled) {
        const t = data.find((x) => x.id === templateId) || null;
        setTemplate(t);
      }
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [templateId, navigate]);

  useEffect(() => {
    let cancelled = false;
    api.get('/admin/teams').then((res) => {
      if (cancelled) return;
      const teams = res.data || [];
      const list: OperatoreOption[] = [];
      if (isAdmin()) {
        list.push({ id: 'current-user', name: 'Denise Cruciani' });
      }
      teams.forEach((team: { id: number; username?: string; teamMembers?: Array<{ id: number; name: string }>; team_members?: Array<{ id: number; name: string }> }) => {
        const members = team.teamMembers || team.team_members || [];
        const teamName = team.username || '';
        members.forEach((m: { id: number; name: string }) => {
          list.push({
            id: `${team.id}-${m.id}`,
            name: teamName ? `${m.name} (${teamName})` : m.name,
          });
        });
      });
      setOperators(list);
    }).catch(() => {
      if (!cancelled) setOperators([]);
    });
    return () => { cancelled = true; };
  }, [isAdmin, user?.name]);

  const handleElimina = () => {
    navigate('/admin/genera-buono', { replace: true });
  };

  const handleSalvaECrea = async () => {
    if (!templateId || !template) return;
    if (!operatore.trim()) {
      setSaveError('Seleziona un operatore per procedere.');
      return;
    }
    setSaveError('');
    setSaving(true);
    try {
      const operatoreName = operators.find((o) => o.id === operatore)?.name ?? operatore;
      const scadeAt = validoFino ? `${validoFino}-01` : undefined; // backend: date (primo del mese)
      const { buono } = await buonoService.createBuono({
        template_id: templateId,
        mail: mail.trim() || undefined,
        telefono: telefono.trim() || undefined,
        regalo: regalo || undefined,
        a: per || undefined,
        da: da || undefined,
        operatore: operatoreName.trim(),
        scade_at: scadeAt,
        stato: 'green',
      });
      sessionStorage.setItem('buonoCreatedSuccess', '1');
      navigate('/admin/archivio', { state: { buonoCreated: 'success' }, replace: true });
    } catch (err: any) {
      const msg = err.response?.data?.message || (err.response?.data?.errors ? JSON.stringify(err.response.data.errors) : 'Errore durante il salvataggio.');
      setSaveError(msg);
      setShowResultModal('error');
    } finally {
      setSaving(false);
    }
  };

  if (templateId == null) {
    return null;
  }

  return (
    <AdminLayout activeSection="genera-buono">
      <div className="genera-page genera-crea-page">
        <div className="genera-crea-page-inner">
          {/* Main Content: form + header (design: 1031px) */}
          <div className="genera-crea-main">
            <div className="genera-crea-frame">
            <div className="genera-crea-header">
              <h1 className="genera-crea-title">Crea il tuo buono</h1>
              <p className="genera-crea-subtitle">Scegli il template</p>
            </div>

            {loading && <div className="genera-loading">Caricamento...</div>}
            {!loading && !template && <div className="genera-error">Template non trovato.</div>}
            {!loading && template && (
              <div className="genera-crea-form-inner">
                <div className="genera-crea-form-blocks">
                  {/* Blocco 1: Operatore (obbligatorio) */}
                  <div className="genera-crea-block">
                    <div className="genera-crea-input-wrap genera-crea-select-wrap">
                      <select
                        className="genera-crea-input genera-crea-select"
                        value={operatore}
                        onChange={(e) => { setOperatore(e.target.value); setSaveError(''); }}
                        aria-label="Seleziona operatore"
                        required
                        aria-required="true"
                      >
                        <option value="">Seleziona operatore *</option>
                        {operators.map((op) => (
                          <option key={op.id} value={op.id}>
                            {op.name}
                          </option>
                        ))}
                      </select>
                      <span className="genera-crea-dropdown-icon" aria-hidden>▼</span>
                    </div>
                  </div>

                  {/* Blocco 2: Mail + Telefono */}
                  <div className="genera-crea-block">
                    <div className="genera-crea-row genera-crea-row-fields">
                      <div className="genera-crea-field">
                        <label className="genera-crea-label">Mail a cui inviare il buono</label>
                        <input
                          type="email"
                          className="genera-crea-input"
                          placeholder="Scrivi qui"
                          value={mail}
                          onChange={(e) => setMail(e.target.value)}
                        />
                      </div>
                      <div className="genera-crea-field">
                        <label className="genera-crea-label">Telefono</label>
                        <input
                          type="tel"
                          className="genera-crea-input"
                          placeholder="Scrivi qui"
                          value={telefono}
                          onChange={(e) => setTelefono(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Blocco 3: Regalo, Per, Da, Valido fino a */}
                  <div className="genera-crea-block genera-crea-block-rows">
                    <div className="genera-crea-row genera-crea-row-fields">
                      <div className="genera-crea-field">
                        <label className="genera-crea-label">Regalo (max 100 caratteri)</label>
                        <input
                          type="text"
                          className="genera-crea-input"
                          placeholder="Cosa regala il cliente"
                          value={regalo}
                          maxLength={100}
                          onChange={(e) => setRegalo(e.target.value)}
                        />
                        <div className="genera-crea-char-counter">
                          {regalo.length}/100
                        </div>
                      </div>
                      <div className="genera-crea-field">
                        <label className="genera-crea-label">Per (max 50 caratteri)</label>
                        <input
                          type="text"
                          className="genera-crea-input"
                          placeholder="Scrivi qui"
                          value={per}
                          maxLength={50}
                          onChange={(e) => setPer(e.target.value)}
                        />
                        <div className="genera-crea-char-counter">
                          {per.length}/50
                        </div>
                      </div>
                    </div>
                    <div className="genera-crea-row genera-crea-row-fields">
                      <div className="genera-crea-field">
                        <label className="genera-crea-label">Da (max 50 caratteri)</label>
                        <input
                          type="text"
                          className="genera-crea-input"
                          placeholder="Scrivi qui"
                          value={da}
                          maxLength={50}
                          onChange={(e) => setDa(e.target.value)}
                        />
                        <div className="genera-crea-char-counter">
                          {da.length}/50
                        </div>
                      </div>
                      <div className="genera-crea-field">
                        <label className="genera-crea-label">Valido fino a</label>
                        <input
                          type="month"
                          className="genera-crea-input"
                          value={validoFino}
                          onChange={(e) => setValidoFino(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {saveError && (
                  <p className="genera-crea-save-error" role="alert">{saveError}</p>
                )}
                <div className="genera-crea-actions">
                  <button
                    type="button"
                    className="genera-crea-btn genera-crea-btn-elimina"
                    onClick={handleElimina}
                    disabled={saving}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                      <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Elimina
                  </button>
                  <button
                    type="button"
                    className="genera-crea-btn genera-crea-btn-salva"
                    onClick={handleSalvaECrea}
                    disabled={saving || !operatore.trim()}
                    title={!operatore.trim() ? 'Seleziona un operatore per abilitare' : undefined}
                  >
                    {saving ? 'Salvataggio...' : 'Salva e Crea'}
                  </button>
                </div>
              </div>
            )}
          </div>
          </div>
          {/* Anteprima: colonna fissa 601px (design) */}
          <div className="genera-crea-anteprima">
          <h1 className="genera-crea-anteprima-title">Anteprima</h1>
          {template && (
            <div className="genera-crea-preview-card-wrap">
              <div className="genera-card genera-crea-preview-card">
                <TemplatePreview
                  template={template}
                  resolveImageUrl={resolveImageUrl}
                  previewData={{
                    regalo: regalo || '',
                    per: per || '',
                    da: da || '',
                    validoFino: validoFino || '',
                  }}
                />
              </div>
            </div>
          )}
          </div>
        </div>
        <footer className="genera-crea-footer">
          © 2021 All rights reserved Estetica Cruciani
        </footer>
      </div>

      {/* Modal esito: errore creazione buono (rosso) */}
      {showResultModal === 'error' && (
        <div className="genera-result-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="genera-result-modal-title">
          <div className="genera-result-modal genera-result-modal-error">
            <h3 id="genera-result-modal-title" className="genera-result-modal-title">Errore nella creazione del buono</h3>
            {saveError && <p className="genera-result-modal-detail">{saveError}</p>}
            <button
              type="button"
              className="genera-result-modal-btn"
              onClick={() => { setShowResultModal(null); setSaveError(''); }}
              aria-label="Chiudi"
            >
              Chiudi
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default GeneraBuonoCrea;
